<script setup lang="ts">
    import { reactive } from 'vue'
    import type { UIChatMessage } from 'models'
    import PkMessageParts from './PkMessageParts.vue'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'
    import PkChunkPreview from './PkChunkPreview.vue'
    import PkDocumentHeader from './PkDocumentHeader.vue'

    interface SearchResult {
        id: string
        content: string
        similarity: number
        chunkIndex?: number | null
        document: {
            id: string
            type: string
            name: string | null
            language: string
            source: {
                filename?: string
                url?: string
                imageUrl?: string
                description?: string
            }
        }
    }

    defineProps<{
        message: UIChatMessage
        agentId: string
        canReadDocuments?: boolean
    }>()

    const expandedDocuments = reactive(new Set<string>())
    const expandedChunks = reactive(new Set<string>())

    const toggleDocument = (documentId: string) => {
        if (expandedDocuments.has(documentId)) {
            expandedDocuments.delete(documentId)
        } else {
            expandedDocuments.add(documentId)
        }
    }

    const toggleChunk = (chunkId: string) => {
        if (expandedChunks.has(chunkId)) {
            expandedChunks.delete(chunkId)
        } else {
            expandedChunks.add(chunkId)
        }
    }

    const getMaxSimilarity = (results: SearchResult[]) => {
        return Math.max(...results.map((r) => r.similarity ?? 0), 0)
    }

    const groupResults = (results: SearchResult[]) => {
        const groups = new Map<
            string,
            { document: SearchResult['document']; chunks: SearchResult[] }
        >()
        for (const result of results) {
            const existing = groups.get(result.document.id)
            if (existing) {
                existing.chunks.push(result)
            } else {
                groups.set(result.document.id, {
                    document: result.document,
                    chunks: [result],
                })
            }
        }
        return [...groups.values()]
    }
</script>

<template>
    <PkMessageParts :message="message">
        <!-- Document links for searchInKnowledgeBase tool output -->
        <template
            v-if="canReadDocuments"
            #tool-search-in-knowledge-base="{ part }">
            <div
                v-if="
                    (part.output as { results: SearchResult[] })?.results
                        ?.length
                "
                class="border-t border-surface-3">
                <ul class="flex flex-col">
                    <li
                        v-for="group in groupResults(
                            (part.output as { results: SearchResult[] })
                                .results,
                        )"
                        :key="group.document.id"
                        class="border-b border-surface-3 last:border-b-0">
                        <!-- Document header -->
                        <button
                            class="flex items-center gap-8 p-8 w-full text-left hover:bg-surface-1 transition-colors cursor-pointer"
                            @click="toggleDocument(group.document.id)">
                            <PkDocumentHeader
                                :type="group.document.type"
                                :name="
                                    group.document.name ??
                                    group.document.source.filename ??
                                    group.document.id
                                "
                                :url="group.document.source.url"
                                :filename="group.document.source.filename" />
                            <div class="flex items-center gap-4 shrink-0">
                                <span
                                    v-if="group.chunks.length > 1"
                                    class="text-10 text-word-4 bg-surface-2 rounded px-4 py-2">
                                    {{ group.chunks.length }}
                                </span>
                                <span
                                    class="text-10 text-word-4 bg-surface-2 rounded px-4 py-2 uppercase">
                                    {{ group.document.language }}
                                </span>
                                <VvButton
                                    modifiers="action-quiet-small"
                                    icon="ri:eye-line"
                                    class="text-10"
                                    :title="$t('action.view')"
                                    :to="{
                                        name: '/backoffice/agents/[id]/documents/[documentId]',
                                        params: {
                                            id: agentId,
                                            documentId: group.document.id,
                                        },
                                    }"
                                    @click.stop />
                                <VvIcon
                                    :name="
                                        expandedDocuments.has(group.document.id)
                                            ? 'ri:arrow-up-s-line'
                                            : 'ri:arrow-down-s-line'
                                    "
                                    class="text-16 text-word-3" />
                            </div>
                        </button>

                        <!-- Expanded chunks -->
                        <div
                            v-if="expandedDocuments.has(group.document.id)"
                            class="border-t border-surface-3">
                            <div
                                v-if="group.document.source.description"
                                class="px-8 py-4 text-10 text-word-3 bg-surface-1 border-b border-surface-3">
                                {{ group.document.source.description }}
                            </div>
                            <div
                                v-for="chunk in group.chunks"
                                :key="chunk.id"
                                class="border-b border-surface-3 last:border-b-0">
                                <button
                                    class="flex items-center gap-8 px-8 py-6 w-full text-left hover:bg-surface-1 transition-colors cursor-pointer"
                                    @click="toggleChunk(chunk.id)">
                                    <PkChunkPreview
                                        :content="chunk.content"
                                        :similarity="chunk.similarity"
                                        :max-similarity="
                                            getMaxSimilarity(
                                                (
                                                    part.output as {
                                                        results: SearchResult[]
                                                    }
                                                ).results,
                                            )
                                        "
                                        :expanded="
                                            expandedChunks.has(chunk.id)
                                        " />
                                </button>
                                <div
                                    v-if="expandedChunks.has(chunk.id)"
                                    class="px-8 py-6 bg-surface-1 border-t border-surface-3">
                                    <PkStreamingMarkdown
                                        :markdown="chunk.content"
                                        class="wysiwyg text-10" />
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </template>

        <!-- Sub-agent / generic worker accordion — exposes a nested slot for
             app-specific content. Configured sub-agents carry subAgentId +
             subAgentName in the output; generic workers are labeled by the
             objective from the tool call input. -->
        <template #subagent="{ part }">
            <VvAccordion
                v-if="
                    part.output?.reasoningChatId &&
                    (part.output?.subAgentId || part.input?.objective)
                "
                :title="
                    part.output.subAgentId
                        ? $t('label.subAgent')
                        : $t('agent.tool.runWorker')
                "
                modifiers="details"
                class="border-t border-surface-3">
                <div class="flex justify-between items-center gap-4 min-w-0">
                    <strong class="font-semibold text-12 truncate">
                        {{ part.output.subAgentName ?? part.input?.objective }}
                    </strong>
                    <VvButton
                        modifiers="action-small"
                        icon="ri:eye-line"
                        class="text-10 shrink-0"
                        :title="$t('action.view')"
                        :to="{
                            name: '/frontoffice/[agentId]/reasoning-logs',
                            params: { agentId },
                            query: { logId: part.output.reasoningChatId },
                        }" />
                </div>
                <!-- For a configured sub-agent the reasoning log lives under the
                     child agent (subAgentId); a generic worker logs under this
                     (parent) agent, so fall back to agentId. -->
                <slot
                    name="subagent-details"
                    :reasoning-chat-id="part.output.reasoningChatId"
                    :sub-agent-id="part.output.subAgentId ?? agentId" />
            </VvAccordion>
        </template>
    </PkMessageParts>
</template>
